import { hmac } from "@noble/hashes/hmac.js";
import { sha1 } from "@noble/hashes/legacy.js";
import { sha256, sha512 } from "@noble/hashes/sha2.js";
import { base32Decode, base32Encode } from "./base32.js";
import { CryptoError } from "./errors.js";
export const TOTP_ALGORITHMS = ["SHA1", "SHA256", "SHA512"];
export const TOTP_DEFAULTS = { algorithm: "SHA1", digits: 6, period: 30 };
const MIN_DIGITS = 6;
const MAX_DIGITS = 8;
const MIN_PERIOD = 10;
const MAX_PERIOD = 300;
/** 80 bits; anything shorter is a typo, not a real secret. */
const MIN_SECRET_BYTES = 10;
function invalid(message) {
    return new CryptoError("invalid-input", message);
}
function isTotpAlgorithm(value) {
    return TOTP_ALGORITHMS.some((a) => a === value);
}
function decodeSecret(raw) {
    const secret = base32Decode(raw);
    if (secret.length < MIN_SECRET_BYTES) {
        throw invalid("TOTP secret is too short.");
    }
    return secret;
}
function intParam(params, name, fallback, min, max) {
    const raw = params.get(name);
    if (raw === null || raw === "") {
        return fallback;
    }
    if (!/^\d+$/.test(raw)) {
        throw invalid(`Invalid TOTP ${name}.`);
    }
    const value = Number(raw);
    if (value < min || value > max) {
        throw invalid(`TOTP ${name} out of range.`);
    }
    return value;
}
function safeDecode(value) {
    try {
        return decodeURIComponent(value);
    }
    catch {
        return value;
    }
}
function parseUri(input) {
    // URL() does not parse the authority of unknown schemes consistently across engines.
    const match = /^otpauth:\/\/([^/?#]*)\/([^?#]*)(?:\?([^#]*))?/i.exec(input);
    if (match === null) {
        throw invalid("Malformed otpauth URI.");
    }
    const kind = (match[1] ?? "").toLowerCase();
    if (kind !== "totp") {
        throw invalid("Only time-based (TOTP) codes are supported.");
    }
    const label = safeDecode(match[2] ?? "");
    const params = new URLSearchParams(match[3] ?? "");
    const secretRaw = params.get("secret");
    if (secretRaw === null || secretRaw === "") {
        throw invalid("otpauth URI has no secret.");
    }
    const algorithmRaw = (params.get("algorithm") ?? TOTP_DEFAULTS.algorithm).toUpperCase().replace("-", "");
    if (!isTotpAlgorithm(algorithmRaw)) {
        throw invalid("Unsupported TOTP algorithm.");
    }
    const colon = label.indexOf(":");
    const labelIssuer = colon >= 0 ? label.slice(0, colon).trim() : "";
    const account = (colon >= 0 ? label.slice(colon + 1) : label).trim();
    return {
        secret: decodeSecret(secretRaw),
        algorithm: algorithmRaw,
        digits: intParam(params, "digits", TOTP_DEFAULTS.digits, MIN_DIGITS, MAX_DIGITS),
        period: intParam(params, "period", TOTP_DEFAULTS.period, MIN_PERIOD, MAX_PERIOD),
        issuer: (params.get("issuer") ?? labelIssuer).trim(),
        account
    };
}
/** Parses an otpauth URI or a bare base32 secret. Throws CryptoError("invalid-input"). */
export function parseTotp(input) {
    const trimmed = input.trim();
    if (trimmed === "") {
        throw invalid("Empty TOTP input.");
    }
    if (/^otpauth:/i.test(trimmed)) {
        return parseUri(trimmed);
    }
    return {
        secret: decodeSecret(trimmed),
        algorithm: TOTP_DEFAULTS.algorithm,
        digits: TOTP_DEFAULTS.digits,
        period: TOTP_DEFAULTS.period,
        issuer: "",
        account: ""
    };
}
export function isValidTotp(input) {
    try {
        parseTotp(input);
        return true;
    }
    catch {
        return false;
    }
}
function mac(algorithm, key, msg) {
    switch (algorithm) {
        case "SHA1":
            return hmac(sha1, key, msg);
        case "SHA256":
            return hmac(sha256, key, msg);
        case "SHA512":
            return hmac(sha512, key, msg);
    }
}
function byteAt(buf, index) {
    const value = buf[index];
    if (value === undefined) {
        throw invalid("Unreachable digest index.");
    }
    return value;
}
/** RFC 4226 HOTP value for one counter. */
export function hotp(params, counter) {
    const msg = new Uint8Array(8);
    let rest = counter;
    for (let i = 7; i >= 0; i -= 1) {
        msg[i] = rest % 256;
        rest = Math.floor(rest / 256);
    }
    const digest = mac(params.algorithm, params.secret, msg);
    const offset = byteAt(digest, digest.length - 1) & 0x0f;
    const bin = ((byteAt(digest, offset) & 0x7f) * 0x1000000) +
        (byteAt(digest, offset + 1) << 16) +
        (byteAt(digest, offset + 2) << 8) +
        byteAt(digest, offset + 3);
    return (bin % 10 ** params.digits).toString().padStart(params.digits, "0");
}
export function totpCodeAt(params, nowMs) {
    return hotp(params, Math.floor(nowMs / 1000 / params.period));
}
export function totpState(params, nowMs) {
    const seconds = Math.floor(nowMs / 1000);
    return {
        code: totpCodeAt(params, nowMs),
        remaining: params.period - (seconds % params.period),
        period: params.period
    };
}
/** Canonical otpauth URI, e.g. to normalize a manually entered secret. */
export function totpUri(params) {
    const label = params.issuer !== "" && params.account !== ""
        ? `${params.issuer}:${params.account}`
        : params.account !== "" ? params.account : params.issuer;
    const query = new URLSearchParams({
        secret: base32Encode(params.secret),
        algorithm: params.algorithm,
        digits: String(params.digits),
        period: String(params.period)
    });
    if (params.issuer !== "") {
        query.set("issuer", params.issuer);
    }
    return `otpauth://totp/${encodeURIComponent(label)}?${query.toString()}`;
}
/** "123456" → "123 456", "12345678" → "1234 5678". */
export function formatTotpCode(code) {
    const half = Math.ceil(code.length / 2);
    return code.length >= 6 ? `${code.slice(0, half)} ${code.slice(half)}` : code;
}
//# sourceMappingURL=totp.js.map